// The onboarding wizard's data model. ONE question: the build
// choice, what the user wants to start with. The two bring-your-own-stack agent
// paths (SIP Trunks / Audio Streaming) expand an inline framework probe under the
// selected option, asking which stack they'll connect.
//
// The business-type question that used to come first is gone, along with
// step-business.tsx and the progress bar it needed. `business_type` survives in
// the payload type only (see types.ts).

import { REGIONS } from 'v2/configs/constants'
import type { BuildChoice, Framework, WizardState } from 'v2/shared/onboarding/types'
import { getLocalAuth } from 'v2/utils'
import { BuildGoal, type OnboardingFormData } from '../types'

// Core rejects '<' and '>' in the payload (the injected no_unsafe_chars guard on
// every declared string field), so the free-text answer is stripped rather than
// left to 400 mid-onboarding. Applied on input and again when building the body.
export const stripUnsafeChars = (value: string): string => value.replace(/[<>]/g, '')

// The answer model itself lives in shared/onboarding (the product banners
// personalise from it); this file owns the wizard's UI options on top of it.
export { INITIAL_WIZARD_STATE } from 'v2/shared/onboarding/types'
export type { BuildChoice, Framework, WizardState }

export interface BuildOption {
  value: BuildChoice
  title: string
  description: string
  // `tone` is required so a badge always declares what it means. 'enterprise' is
  // a restriction: a neutral grey pill with a lock (an amber warning tone was
  // tried and dropped — a gated feature is not an error). 'studio' names the
  // product behind the option, in the design system's vibing gradient. 'neutral'
  // also names the product behind the option (SIP Trunking, Plivo XML, …) but in
  // the quiet grey pill WITHOUT the lock — the lock is reserved for restrictions,
  // so a neutral badge must never look like one. `tooltip` hangs extra context
  // off the badge (the enterprise badge's "talk to sales" line).
  badge?: { label: string; tone: 'enterprise' | 'studio' | 'neutral'; tooltip?: string }
}

// The wizard's only question: the top-level choices, shown as one flat list. The no-code path leads
// and carries the "AI Agent Studio" badge; the emphasis is the badge alone, so
// no card gets a louder RESTING style than a selected card has.
//
// Titles lead with the USE CASE; the Plivo product behind each option lives in a
// neutral badge beside the title (SIP Trunking, WebSocket / Audio Streaming,
// Plivo XML) — the badge column is where the product name scans fastest, and it
// frees the title to say what the option is FOR. Descriptions are one sentence
// each and may wrap; the SIP one deliberately names the stacks it connects.
//
// These four are region-independent, so they are safe as module-level consts:
// nothing here reads localStorage. Only messaging (below, inside buildOptions)
// depends on the region.
const VOICE_AI: BuildOption = {
  value: 'voice-ai',
  title: 'No-code Voice Agent',
  description: 'Build and launch a voice agent in our visual builder. No code needed.',
  badge: { label: 'AI Agent Studio', tone: 'studio' },
}

const SIP: BuildOption = {
  value: 'sip',
  title: 'Connect Your Existing Voice Agent',
  description:
    'Already running an agent on Vapi, Retell, ElevenLabs, OpenAI, LiveKit, or your own stack? Give it phone connectivity over SIP.',
  badge: { label: 'SIP Trunking', tone: 'neutral' },
}

const AUDIO: BuildOption = {
  value: 'audio',
  title: 'Build Your Voice Agent Programmatically',
  description: 'Stream live call audio to your server and orchestrate any STT, LLM, and TTS.',
  badge: { label: 'WebSocket, Audio Streaming', tone: 'neutral' },
}

const VOICE_API: BuildOption = {
  value: 'voice-api',
  title: 'Add Human Voice Calling to Your Applications',
  description: 'Enable call forwarding, call conferencing, or click-to-call within your app.',
  badge: { label: 'Plivo XML', tone: 'neutral' },
}

// A FUNCTION, not a const array, because both the messaging description AND the
// option ORDER depend on the org's region, and `getLocalAuth()` reads
// localStorage — evaluating it at module load would capture whatever was there
// when the chunk was first imported, which on a fresh signup is nothing. Same
// call-time treatment `resolveDestination` already gives the region below.
//
// The region is read ONCE and threaded into both decisions, rather than each one
// calling getLocalAuth() again: two reads of the same key can only agree, so a
// second call is just a second thing to keep in sync.
//
// Two literal arrays: the array IS the order, so there is nothing to rank and no
// fallback to reason about. An option missing from one list fails the
// exact-order tests in step-build.test.tsx.
export const buildOptions = (): BuildOption[] => {
  const isIndia = getLocalAuth()?.region === REGIONS.IN

  const messaging: BuildOption = {
    value: 'messaging',
    title: 'Messaging',
    // Indian-region orgs get WhatsApp ONLY: SMS and verification are not offered
    // there, and /compliance/* is not even a registered route for them (see
    // resolveDestination, which sends them to /whatsapp/numbers for the same
    // reason). Naming OTPs-over-SMS to an Indian org would advertise products
    // the account cannot reach, on the first screen after signup.
    // The plan requirement is the badge's job (plus its tooltip), not the
    // description's — it was spelled out in both for a moment and read as the
    // card saying it twice.
    description: isIndia
      ? 'Send OTPs, alerts, and campaigns over WhatsApp, at scale.'
      : 'Send OTPs, alerts, and campaigns over SMS and WhatsApp, at scale.',
    badge: {
      label: 'Enterprise Plan',
      tone: 'enterprise',
      tooltip: 'Messaging is available on Enterprise. Talk to sales.',
    },
  }

  // India leads its bring-your-own-stack pair with Audio Streaming (audio at 2,
  // SIP at 3); every other region keeps SIP ahead of audio. Order only — the five
  // options, their copy and their badges are identical in both regions.
  return isIndia
    ? [VOICE_AI, AUDIO, SIP, VOICE_API, messaging]
    : [VOICE_AI, SIP, AUDIO, VOICE_API, messaging]
}

export interface FrameworkOption {
  value: Framework
  label: string
  // When set, selecting this option reveals a free-text field with this
  // placeholder so we can capture the user's specific stack.
  detailPlaceholder?: string
}

// The bring-your-own answer, shared by both lists and always LAST: the named
// platforms are the common cases, and the escape hatch reads as the fallback
// only when it sits after everything it is a fallback for.
const CUSTOM_STACK: FrameworkOption = {
  value: 'custom',
  label: 'Custom stack',
  detailPlaceholder: 'What are you running? e.g. in-house, Vapi, Retell, Ultravox…',
}

// The inline framework probe's platform lists (SIP trunking / audio streaming
// only) — a shared core plus per-build additions, per the transport note below.
// Who gets listed: POPULAR platforms we do NOT compete with. That
// rules out Retell, Vapi and ElevenLabs — voice-agent platforms in our own
// market — and leaves orchestration frameworks and model providers, which are
// complements. Anything unlisted arrives by name through the Custom stack
// option's free-text field. (The original probe also offered "Not for voice
// agents"; that opt-out was cut, though stored answers naming it are still
// honoured — see isNotForVoiceAgents.)
// Pipecat is the only platform on BOTH lists: it is an orchestration framework
// you run yourself, so it is reachable over either transport. Everything else is
// exclusive to one path.
const PIPECAT: FrameworkOption = { value: 'pipecat', label: 'Pipecat' }

// The lists diverge by the TRANSPORT each vendor actually offers.
const SIP_FRAMEWORKS: FrameworkOption[] = [
  // "LiveKit Cloud" (not bare "LiveKit"), matching the Create Trunk picker the
  // SIP path meets one page later. SIP-only, and deliberately not on the audio
  // list: LiveKit Cloud is a hosted platform you point a trunk AT, so it is not
  // something you reach by streaming raw call audio to your own server.
  { value: 'livekit', label: 'LiveKit Cloud' },
  PIPECAT,
  // OpenAI is named for the product, matching what their SIP docs call it: the
  // Realtime API (sip.api.openai.com). xAI carries the parenthetical because
  // that is exactly how the Create Trunk platform picker labels it one page
  // later (api-console/applications/platform-config.ts, #3642) — the same
  // matching rule as "LiveKit Cloud" above.
  { value: 'openai', label: 'OpenAI Realtime' },
  { value: 'xai', label: 'xAI (SpaceXAI)' },
  CUSTOM_STACK,
]

const AUDIO_FRAMEWORKS: FrameworkOption[] = [
  PIPECAT,
  // Google's realtime audio product is the Live API, and it is WebSocket-based —
  // which is why it belongs to the audio-streaming path and not the SIP one.
  { value: 'gemini', label: 'Gemini Live' },
  CUSTOM_STACK,
]

export const frameworkOptions = (build: BuildChoice | ''): FrameworkOption[] =>
  build === 'audio' ? AUDIO_FRAMEWORKS : SIP_FRAMEWORKS

export interface StepProps {
  state: WizardState
  update: (patch: Partial<WizardState>) => void
}

// Only the two bring-your-own-stack agent paths get the inline framework probe.
// Voice API is deliberately excluded: the probe asks which agent framework is
// being connected, and a Voice API user is not connecting one.
export const needsFrameworkStep = (build: BuildChoice | ''): boolean =>
  build === 'sip' || build === 'audio'

// Where "Get started" navigates the user on completion. Lands on the plain
// product page (no auto-opened create sheet/dialog, no jump into the builder)
// so the onboarding banner on that page is the first thing they see.
export const resolveDestination = (state: WizardState): string => {
  switch (state.build) {
    case 'sip':
      return '/sip-trunking/inbound'
    // Both land on Voice Applications: it is where the answer_url that handles
    // a call is configured, for an audio-stream agent and a Voice API app alike.
    case 'audio':
    case 'voice-api':
      return '/applications'
    case 'messaging':
      // /compliance/* is not registered for Indian-region orgs (routes/main.tsx
      // isComplianceHidden), so it would land new signups on the 404 page as
      // their first screen. WhatsApp is the messaging surface that exists there.
      return getLocalAuth()?.region === REGIONS.IN ? '/whatsapp/numbers' : '/compliance/drafts'
    default:
      return '/agents'
  }
}

// The wizard is complete when a build is chosen — plus the follow-up answer when that
// build asks for one (both follow-ups render inline under the selected option).
// Lives here rather than in the component so it can be tested directly:
// useProfileQuery makes the wizard itself an unreliable seam in the full suite
// (CLAUDE.md failure mode 17).
export const isComplete = (state: WizardState): boolean => {
  if (!state.build) return false
  // The agent description is OPTIONAL: it is qualification data for the
  // marketing zap, not an input the flow consumes, and gating Get started on
  // it just extorts junk answers. Whatever is typed still ships via
  // buildOnboardingPayload below.
  return needsFrameworkStep(state.build) ? !!state.framework : true
}

// Each free-text answer ships under its OWN key (core#1367 added
// agent_description precisely so a stack name and a task description are
// distinguishable downstream without joining on use_case/build). They can never
// coexist: switching builds resets the downstream answers. Both are trimmed and
// stripped here, at the submit choke point, never on change (CLAUDE.md failure
// mode 21).
const cleanFreeText = (value: string): string | undefined =>
  stripUnsafeChars(value).trim() || undefined

// Maps the wizard's build choice onto the backend's onboarding use_case values
// (the contract the legacy questionnaire used to fill).
export const BUILD_TO_USE_CASE: Record<BuildChoice, BuildGoal> = {
  'voice-ai': BuildGoal.VOICE_AI_AGENTS,
  sip: BuildGoal.SIP_TRUNKING,
  audio: BuildGoal.AUDIO_STREAMING,
  'voice-api': BuildGoal.VOICE_API,
  messaging: BuildGoal.MESSAGING,
}

// The POST /orgs/onboarding body for a finished wizard. `businessType` stays a
// PARAMETER rather than a WizardState field: the question that filled it was
// removed, the wizard passes '', and nothing that reads WizardState (the product
// banners) ever consumed it. Keeping the parameter keeps the payload's shape a
// contract the caller supplies rather than one this file invents.
//
// The free text is only stripped of angle brackets, which core rejects outright.
// It is deliberately NOT truncated — core caps none of these fields, and silently
// cutting a qualification answer short loses exactly the detail it asked for.
export const buildOnboardingPayload = (
  state: WizardState,
  businessType: string,
): OnboardingFormData => ({
  business_type: businessType || undefined,
  use_case: state.build ? BUILD_TO_USE_CASE[state.build] : undefined,
  build: state.build || undefined,
  framework: state.framework || undefined,
  framework_detail: cleanFreeText(state.frameworkDetail),
  agent_description: state.build === 'voice-ai' ? cleanFreeText(state.agentDescription) : undefined,
})
